import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { edgeBezierControls, sampleEdgeBezier } from "@/document/operations/vectorGeometry";
import { lerpPoint, splitEdge } from "@/document/operations/vectorEdgeSplit";
import { createId } from "@/lib/ids";

const ARC_LENGTH_SAMPLES = 64;
const EPSILON = 1e-9;

export interface SubdivideEdgesOptions {
  /** Number of segments each selected edge should become. Minimum 2. */
  segments: number;
  /** When true, connect corresponding new vertices across every pair of selected edges. */
  connect?: boolean;
  /** Optional id factory for deterministic previews / commits. */
  createId?: (prefix: string) => string;
}

export interface SubdivideEdgesResult {
  network: VectorNetwork;
  /** New subdivision vertices in from→to order for each original edge id. */
  newVertexIdsByEdge: Record<string, string[]>;
  /** All newly created subdivision vertex ids. */
  newVertexIds: string[];
  /** Connector edge ids added when `connect` is true. */
  connectorEdgeIds: string[];
  /** Original edge ids that were replaced. */
  changedEdgeIds: string[];
}

/**
 * Builds an alternate network where each selected edge is subdivided into
 * `segments` equal-arc-length pieces. Optionally adds straight connectors
 * between corresponding new vertices across every selected-edge pair.
 */
export function subdivideEdges(
  network: VectorNetwork,
  edgeIds: string[],
  options: SubdivideEdgesOptions,
): SubdivideEdgesResult {
  const segments = Math.max(2, Math.floor(options.segments));
  const makeId = options.createId ?? createId;
  const selected = uniqueExistingEdges(network, edgeIds);
  if (selected.length === 0) {
    return {
      network: cloneNetwork(network),
      newVertexIdsByEdge: {},
      newVertexIds: [],
      connectorEdgeIds: [],
      changedEdgeIds: [],
    };
  }

  // Capture original endpoints before replacement for geometric orientation.
  const originalEndpoints = new Map(
    selected.map(edge => {
      const from = network.vertices.find(vertex => vertex.id === edge.from.vertexId);
      const to = network.vertices.find(vertex => vertex.id === edge.to.vertexId);
      return [edge.id, { from, to }] as const;
    }),
  );

  const next = cloneNetwork(network);
  const newVertexIdsByEdge: Record<string, string[]> = {};
  const newVertexIds: string[] = [];
  const changedEdgeIds: string[] = [];
  const replacementByOriginal = new Map<string, VectorEdge[]>();

  for (const edge of selected) {
    const params = arcLengthParamsForEdge(next, edge.id, segments);
    const split = subdivideSingleEdge(next, edge, params, makeId);
    if (!split) {
      continue;
    }
    changedEdgeIds.push(edge.id);
    newVertexIdsByEdge[edge.id] = split.vertexIds;
    newVertexIds.push(...split.vertexIds);
    replacementByOriginal.set(edge.id, split.edges);
    next.vertices.push(...split.vertices);
  }

  if (changedEdgeIds.length > 0) {
    const replaced = new Set(changedEdgeIds);
    next.edges = next.edges.flatMap(edge => {
      if (!replaced.has(edge.id)) {
        return [edge];
      }
      return replacementByOriginal.get(edge.id) ?? [edge];
    });
  }

  const connectorEdgeIds: string[] = [];
  if (options.connect && selected.length >= 2) {
    const entries = selected.map(edge => {
      const ends = originalEndpoints.get(edge.id);
      return {
        vertexIds: newVertexIdsByEdge[edge.id] ?? [],
        from: ends?.from,
        to: ends?.to,
      };
    });

    for (let i = 0; i < entries.length; i += 1) {
      for (let j = i + 1; j < entries.length; j += 1) {
        const left = entries[i]!;
        const right = entries[j]!;
        const count = Math.min(left.vertexIds.length, right.vertexIds.length);
        if (count === 0) {
          continue;
        }
        // Decide correspondence orientation independently for each edge pair so
        // that connectors align tip-to-tail (nearest endpoints) and never cross.
        const rightIds = shouldReversePair(left, right)
          ? [...right.vertexIds].reverse()
          : right.vertexIds;
        for (let index = 0; index < count; index += 1) {
          const fromVertexId = left.vertexIds[index]!;
          const toVertexId = rightIds[index]!;
          if (hasEdge(next, fromVertexId, toVertexId)) {
            continue;
          }
          const edgeId = makeId("edge");
          next.edges.push({
            id: edgeId,
            from: { vertexId: fromVertexId },
            to: { vertexId: toVertexId },
            directed: false,
          });
          connectorEdgeIds.push(edgeId);
        }
      }
    }
  }

  return {
    network: next,
    newVertexIdsByEdge,
    newVertexIds,
    connectorEdgeIds,
    changedEdgeIds,
  };
}

/** Equal-arc-length parameter values in (0, 1) for subdividing into `segments` pieces. */
export function arcLengthParamsForEdge(network: VectorNetwork, edgeId: string, segments: number): number[] {
  const edge = network.edges.find(candidate => candidate.id === edgeId);
  if (!edge || segments < 2) {
    return [];
  }
  const from = network.vertices.find(vertex => vertex.id === edge.from.vertexId);
  const to = network.vertices.find(vertex => vertex.id === edge.to.vertexId);
  if (!from || !to) {
    return [];
  }

  const samples = buildArcLengthSamples(from, to, edge.from.handle, edge.to.handle);
  const total = samples.at(-1)?.length ?? 0;
  if (total <= EPSILON) {
    return Array.from({ length: segments - 1 }, (_, index) => (index + 1) / segments);
  }

  const params: number[] = [];
  for (let index = 1; index < segments; index += 1) {
    params.push(parameterAtArcLength(samples, (total * index) / segments));
  }
  return params;
}

/**
 * Split an edge at absolute original-edge parameters by repeatedly cutting the
 * current left child from high t to low t. After a cut at absolute T, the left
 * child represents [0, T] of the original, so the next absolute t maps to t/T.
 */
function subdivideSingleEdge(
  network: VectorNetwork,
  originalEdge: VectorEdge,
  params: number[],
  makeId: (prefix: string) => string,
): { vertices: VectorVertex[]; vertexIds: string[]; edges: VectorEdge[] } | undefined {
  if (params.length === 0) {
    return undefined;
  }

  const descending = [...params].sort((a, b) => b - a);
  const vertices: VectorVertex[] = [];
  const vertexIds: string[] = [];
  const rightEdges: VectorEdge[] = [];
  let current = structuredClone(originalEdge);
  let upper = 1;

  for (const absoluteT of descending) {
    const localT = upper <= EPSILON ? 0 : Math.max(0, Math.min(1, absoluteT / upper));
    const vertexId = makeId("vertex");
    const firstEdgeId = makeId("edge");
    const secondEdgeId = makeId("edge");
    const workingVertices = [...network.vertices, ...vertices];
    const from = workingVertices.find(vertex => vertex.id === current.from.vertexId);
    const to = workingVertices.find(vertex => vertex.id === current.to.vertexId);
    const fallback = from && to ? lerpPoint(from, to, localT) : { x: 0, y: 0 };
    const split = splitEdge(current, { vertices: workingVertices }, vertexId, firstEdgeId, secondEdgeId, localT, fallback);
    vertices.push(split.vertex);
    vertexIds.unshift(split.vertex.id);
    rightEdges.unshift(split.secondEdge);
    current = split.firstEdge;
    upper = absoluteT;
  }

  return {
    vertices,
    vertexIds,
    edges: [current, ...rightEdges],
  };
}

/**
 * Decide whether the right edge's new vertices should be reversed so that the
 * two edges correspond tip-to-tail. Comparing endpoint distances keeps points
 * that share (or sit nearest) a common vertex paired together, which yields
 * non-crossing connectors for arbitrary edge orientations (e.g. triangle loops).
 */
function shouldReversePair(
  left: { from?: VectorVertex; to?: VectorVertex },
  right: { from?: VectorVertex; to?: VectorVertex },
): boolean {
  if (!left.from || !left.to || !right.from || !right.to) {
    return false;
  }
  const same = distance(left.from, right.from) + distance(left.to, right.to);
  const flipped = distance(left.from, right.to) + distance(left.to, right.from);
  return flipped < same;
}

function buildArcLengthSamples(
  from: VectorVertex,
  to: VectorVertex,
  fromHandle: VectorEdge["from"]["handle"],
  toHandle: VectorEdge["to"]["handle"],
): Array<{ t: number; length: number }> {
  const controls = edgeBezierControls(from, to, fromHandle, toHandle);
  if (!controls) {
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    return [
      { t: 0, length: 0 },
      { t: 1, length },
    ];
  }

  const points = sampleEdgeBezier(from, to, fromHandle, toHandle, ARC_LENGTH_SAMPLES);
  const samples: Array<{ t: number; length: number }> = [{ t: 0, length: 0 }];
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const prev = points[index - 1]!;
    const point = points[index]!;
    total += Math.hypot(point.x - prev.x, point.y - prev.y);
    samples.push({ t: index / (points.length - 1), length: total });
  }
  return samples;
}

function parameterAtArcLength(samples: Array<{ t: number; length: number }>, targetLength: number): number {
  const total = samples.at(-1)?.length ?? 0;
  const clamped = Math.max(0, Math.min(total, targetLength));
  let low = 0;
  let high = samples.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (samples[mid]!.length < clamped) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  const right = samples[low] ?? samples.at(-1)!;
  const left = samples[Math.max(0, low - 1)] ?? samples[0]!;
  const span = right.length - left.length;
  return span <= EPSILON ? right.t : left.t + ((clamped - left.length) / span) * (right.t - left.t);
}

function uniqueExistingEdges(network: VectorNetwork, edgeIds: string[]): VectorEdge[] {
  const seen = new Set<string>();
  const edges: VectorEdge[] = [];
  for (const edgeId of edgeIds) {
    if (seen.has(edgeId)) {
      continue;
    }
    const edge = network.edges.find(candidate => candidate.id === edgeId);
    if (!edge) {
      continue;
    }
    seen.add(edgeId);
    edges.push(edge);
  }
  return edges;
}

function cloneNetwork(network: VectorNetwork): VectorNetwork {
  return {
    fillRule: network.fillRule,
    fillOverrideTable: network.fillOverrideTable ? structuredClone(network.fillOverrideTable) : undefined,
    regions: network.regions.map(region => ({ ...region, edgeIds: [...region.edgeIds] })),
    vertices: network.vertices.map(vertex => ({ ...vertex })),
    edges: network.edges.map(edge => ({
      ...edge,
      from: { vertexId: edge.from.vertexId, handle: edge.from.handle ? { ...edge.from.handle } : undefined },
      to: { vertexId: edge.to.vertexId, handle: edge.to.handle ? { ...edge.to.handle } : undefined },
    })),
  };
}

function hasEdge(network: VectorNetwork, fromVertexId: string, toVertexId: string): boolean {
  return network.edges.some(edge =>
    (edge.from.vertexId === fromVertexId && edge.to.vertexId === toVertexId)
    || (edge.from.vertexId === toVertexId && edge.to.vertexId === fromVertexId),
  );
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
